import axios from 'axios';
import { jwtDecode } from 'jwt-decode';
import { loadingState } from '@/core/loader/loading';
import { AuthService } from '../../service/AuthService';
import { API_BASE_URL } from './constants';
let toastInstance = null;

// Register toast from setup()
export function setupAxiosInterceptors(toast) {
    toastInstance = toast;
}

function handleResponseToast(response, toast) {
    const isSuccess = !!response.status;

    toast.add({
        severity: isSuccess ? 'success' : 'error',
        summary: isSuccess ? 'Success' : 'Error',
        detail: response.message || (isSuccess ? 'Record saved successfully' : 'Something went wrong'),
        life: 4000
    });

    return isSuccess;
}

const axiosInstance = axios.create({
    baseURL: API_BASE_URL,
    timeout: 300000
});

// Request interceptor
axiosInstance.interceptors.request.use(
    (config) => {
        loadingState.start();
        const token = localStorage.getItem('token');
        if (token) {
            if (isTokenExpired(token)) {
                AuthService.logout();
                window.location.href = '/login';
                return Promise.reject(new Error('Token expired'));
            }

            config.headers['Authorization'] = 'Bearer ' + token;
        }
        return config;
    },
    () => {
        loadingState.stop();
        return Promise.reject(); // suppress details
    }
);

// Response interceptor
axiosInstance.interceptors.response.use(
    (response) => {
        loadingState.stop();
        const res = response.data;
        const isStructured = 'status' in res && 'data' in res;

        if (isStructured) {
            const skipToast = res.statusCode === 200 && (!res.message || res.message.trim() === '');

            if (toastInstance && !skipToast) {
                handleResponseToast(res, toastInstance);
            }

            if (res.status === true) {
                return res.data;
            } else {
                return Promise.resolve(); // prevent unhandled rejection
            }
        }

        return res; // raw fallback
    },
    (error) => {
        loadingState.stop();

        const message = error.response?.data?.message || '';
        const statusCode = error.response?.status;

        const skipToast = statusCode === 200 && message.trim() === '';

        if (toastInstance && !skipToast) {
            toastInstance.add({
                severity: 'error',
                summary: 'Error',
                detail: message || error.message || 'Request failed',
                life: 4000
            });
        }

        return Promise.resolve(); // suppress console error
    }
);
export function isTokenExpired(token) {
    if (!token) return true; // no token means expired

    try {
        const { exp } = jwtDecode(token);
        if (!exp) return true; // no expiration info, consider expired

        // JWT exp is in seconds, Date.now() in ms
        return Date.now() >= exp * 1000;
    } catch {
        // Invalid token format
        return true;
    }
}
export default axiosInstance;
